import { useState, useRef, useEffect, type FormEvent } from "react";
import { Button } from "./Button";
import { Input } from "./Input";
import { Label } from "./Label";
import { ThemeToggle } from "./Theme";
import { MyDailyDirectLogo } from "./MyDailyDirectLogo";
import {
  ShieldCheck,
  ArrowRight,
  Building2,
  User,
  Sparkles,
  Smartphone,
  CheckCircle2,
  RefreshCw,
  MapPin,
  Navigation,
} from "lucide-react";
import { DocumentUpload } from "./DocumentUpload";

export interface LoginValues {
  tenantId: string;
  userId: string;
  fullName: string;
  isSignUp?: boolean;
  businessName?: string;
  accountType?: string;
  phone?: string;
  city?: string;
  addressLine?: string;
  latitude?: number;
  longitude?: number;
  photoUrl?: string;
  gstNumber?: string;
  otpCode?: string;
}

export interface LoginScreenProps {
  appName: string;
  subtitle?: string;
  /** The persona/role this app operates as, shown to the user for clarity. */
  roleLabel: string;
  defaultTenantId?: string;
  onSubmit: (values: LoginValues) => any;
  onSendOTP?: (phone: string) => Promise<{ isDemoMode: boolean }>;
  onVerifyOTP?: (otpCode: string) => Promise<boolean>;
}

export function LoginScreen({
  appName: _appName,
  subtitle,
  roleLabel,
  defaultTenantId: _defaultTenantId = "mydailydirect",
  onSubmit,
  onSendOTP,
  onVerifyOTP,
}: LoginScreenProps) {
  const tenantId = "mydailydirect";

  // Step state: "phone" -> "otp" -> "profile" (if new account)
  const [step, setStep] = useState<"phone" | "otp" | "profile">("phone");
  const [phone, setPhone] = useState("");
  const [otpDigits, setOtpDigits] = useState<string[]>(["", "", "", "", "", ""]);

  // Profile Sign-Up fields for new numbers (self-registration is exclusively for Retailers)
  const [userId] = useState("");
  const [fullName, setFullName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [city, setCity] = useState("Bengaluru");
  const [addressLine, setAddressLine] = useState("");
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string>("");
  const [locating, setLocating] = useState(false);
  const [gstNumber, setGstNumber] = useState("");
  const accountType = "Retailer";
  const isBuyerPortal =
    roleLabel.toLowerCase().includes("retailer") ||
    roleLabel.toLowerCase().includes("store") ||
    roleLabel.toLowerCase().includes("catalog");

  function handleCaptureLocation() {
    if (!navigator.geolocation) {
      setErrorMsg("Geolocation is not supported by your browser.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude);
        setLongitude(position.coords.longitude);
        setLocating(false);
      },
      () => {
        setLocating(false);
        setErrorMsg("Could not detect GPS location. You can enter your shop address manually.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }

  const [resendTimer, setResendTimer] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Timer tick for Resend OTP
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (step === "otp" && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, resendTimer]);

  // Handle Step 1: Send OTP
  async function handleSendOTP(e?: FormEvent) {
    if (e) e.preventDefault();
    setErrorMsg("");

    const cleanDigits = phone.replace(/\D/g, "");
    if (cleanDigits.length < 10) {
      setErrorMsg("Please enter a valid 10-digit mobile phone number.");
      return;
    }

    setSubmitting(true);
    try {
      if (onSendOTP) {
        await onSendOTP(cleanDigits);
      }
      setStep("otp");
      setResendTimer(30);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Failed to send OTP.");
    } finally {
      setSubmitting(false);
    }
  }

  // Handle Step 2: Verify OTP
  async function handleVerifyOTP(e?: FormEvent) {
    if (e) e.preventDefault();
    setErrorMsg("");

    const code = otpDigits.join("");
    if (code.length < 6) {
      setErrorMsg("Please enter the complete 6-digit OTP code.");
      return;
    }

    setSubmitting(true);
    try {
      let isValid = true;
      if (onVerifyOTP) {
        isValid = await onVerifyOTP(code);
      }

      if (!isValid) {
        setErrorMsg("Invalid OTP code. Please enter the code sent to your phone.");
        setSubmitting(false);
        return;
      }

      // Proceed to Step 3 Profile Setup or submit
      const res = await onSubmit({
        tenantId,
        userId: userId || `user_${phone.replace(/\D/g, "").slice(-4)}`,
        fullName: fullName || "User",
        isSignUp: false,
        businessName,
        accountType,
        phone: `+91 ${phone}`,
        otpCode: code,
      });

      if (res && (res as any).isNewUser) {
        setStep("profile");
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "OTP verification failed.");
    } finally {
      setSubmitting(false);
    }
  }

  // Handle Step 3: Complete Sign-Up Profile Registration
  async function handleProfileSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMsg("");

    if (!fullName.trim()) {
      setErrorMsg("Please enter your full name.");
      return;
    }

    setSubmitting(true);
    try {
      const generatedUserId = userId.trim() || `user_${phone.replace(/\D/g, "").slice(-4)}`;
      await onSubmit({
        tenantId,
        userId: generatedUserId,
        fullName: fullName.trim(),
        isSignUp: true,
        businessName: businessName.trim() || `${fullName.trim()}'s Business`,
        accountType,
        phone: `+91 ${phone}`,
        city,
        addressLine: addressLine.trim() || undefined,
        latitude: latitude ?? undefined,
        longitude: longitude ?? undefined,
        photoUrl: photoUrl || undefined,
        gstNumber,
        otpCode: otpDigits.join(""),
      });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Registration failed.");
    } finally {
      setSubmitting(false);
    }
  }

  // Handle OTP digit box keypress and auto-advance
  function handleOtpDigitChange(index: number, value: string) {
    const digit = value.replace(/\D/g, "").slice(-1);
    const updated = [...otpDigits];
    updated[index] = digit;
    setOtpDigits(updated);

    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  }

  function handleOtpKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  }

  return (
    <div className="flex min-h-dvh bg-background">
      {/* Theme toggle */}
      <div className="absolute right-4 top-4 z-10">
        <ThemeToggle />
      </div>

      {/* Hidden container for Firebase Invisible reCAPTCHA */}
      <div id="recaptcha-container" />

      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-2/5 flex-col bg-primary relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: `linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />

        <div className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-white/5" />
        <div className="absolute -top-20 -right-16 h-64 w-64 rounded-full bg-white/5" />

        <div className="relative z-10 flex flex-col h-full p-12 justify-between">
          {/* Logo #1 (Desktop Hero Header) */}
          <MyDailyDirectLogo size="xl" subtitle={subtitle ?? "Wholesale Commerce & Logistics"} />

          <div className="my-auto space-y-6">
            <div className="space-y-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/70 bg-white/10 px-3 py-1 rounded-full border border-white/20">
                <Sparkles className="h-3.5 w-3.5" /> Firebase Phone OTP Secured
              </span>
              <h2 className="text-3xl lg:text-4xl font-extrabold text-white leading-tight">
                Instant SMS Verification
              </h2>
              <p className="text-sm text-white/80 leading-relaxed">
                Log in securely via 6-digit mobile SMS OTP verification.
              </p>
            </div>

            <div className="space-y-3 pt-2">
              {[
                "Instant SMS OTP Delivery via Firebase",
                "256-Bit Encrypted Mobile Verification",
                "Automatic Account & Profile Authorization",
              ].map((feat) => (
                <div key={feat} className="flex items-center gap-3 text-xs font-medium text-white/90">
                  <div className="h-2 w-2 rounded-full bg-emerald-400 shrink-0" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-white/50 text-xs border-t border-white/10 pt-6">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            <span>256-Bit Encrypted Mobile Verification</span>
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex flex-1 flex-col items-center justify-center p-6 sm:p-12 overflow-y-auto">
        {/* Logo #2 (Form Header) */}
        <div className="flex flex-col items-center gap-2 mb-6 text-center">
          <MyDailyDirectLogo size="lg" subtitle={`${roleLabel} Portal`} />
        </div>

        <div className="w-full max-w-md space-y-6">

          {/* Inline Error Alert */}
          {errorMsg && (
            <div className="p-3.5 rounded-control bg-destructive/10 border border-destructive/30 text-destructive text-xs font-semibold">
              {errorMsg}
            </div>
          )}

          {/* Step 1: Mobile Phone Number Input */}
          {step === "phone" && (
            <form onSubmit={handleSendOTP} className="space-y-5">
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight text-foreground">
                  Sign In with Phone OTP
                </h1>
                <p className="mt-1 text-xs text-muted-foreground">
                  Enter your mobile number to receive a 6-digit verification code.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone-input" className="text-xs font-bold flex items-center gap-1.5">
                  <Smartphone className="h-3.5 w-3.5 text-primary" /> Mobile Phone Number
                </Label>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 px-3 py-2.5 rounded-control border border-border bg-sunken text-xs font-bold text-foreground shrink-0">
                    <span>🇮🇳</span>
                    <span>+91</span>
                  </div>
                  <Input
                    id="phone-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="98765 43210"
                    required
                    className="h-12 font-bold text-base tracking-wide rounded-control"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 font-black text-sm shadow-md bg-cta text-cta-foreground hover:brightness-105 active:scale-[0.99] rounded-control"
                loading={submitting}
              >
                Send Verification OTP
                {!submitting && <ArrowRight className="h-4 w-4 ml-2" />}
              </Button>
            </form>
          )}

          {/* Step 2: 6-Digit OTP Entry */}
          {step === "otp" && (
            <form onSubmit={handleVerifyOTP} className="space-y-5">
              <div>
                <div className="flex items-center justify-between">
                  <h1 className="text-2xl font-extrabold tracking-tight text-foreground">
                    Enter 6-Digit OTP
                  </h1>
                  <button
                    type="button"
                    onClick={() => setStep("phone")}
                    className="text-2xs font-bold text-cta hover:underline"
                  >
                    Change Phone
                  </button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Verification code sent to <strong className="text-foreground">+91 {phone}</strong>
                </p>
              </div>

              {/* 6 Individual Digit Inputs */}
              <div className="flex items-center justify-between gap-2">
                {otpDigits.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => {
                      otpInputRefs.current[idx] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpDigitChange(idx, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                    className="w-12 h-12 text-center text-xl font-extrabold rounded-control border border-border bg-sunken text-foreground focus:border-cta focus:ring-2 focus:ring-cta/20 shadow-xs transition-all outline-none"
                  />
                ))}
              </div>

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Didn't receive code?</span>
                {resendTimer > 0 ? (
                  <span className="font-semibold text-foreground">Resend in {resendTimer}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSendOTP()}
                    className="font-bold text-cta hover:underline flex items-center gap-1"
                  >
                    <RefreshCw className="h-3 w-3" /> Resend OTP
                  </button>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-12 font-black text-sm shadow-md bg-cta text-cta-foreground hover:brightness-105 active:scale-[0.99] rounded-control"
                loading={submitting}
              >
                Verify & Sign In
                {!submitting && <CheckCircle2 className="h-4 w-4 ml-2" />}
              </Button>
            </form>
          )}

          {/* Step 3: New Account Profile Registration */}
          {step === "profile" && (
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight text-foreground">
                  Complete Business Profile
                </h1>
                <p className="mt-1 text-xs text-muted-foreground">
                  OTP verified for <strong className="text-foreground">+91 {phone}</strong>. Enter your trading details.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reg-name" className="text-xs font-bold flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-primary" /> Full Name
                </Label>
                <Input
                  id="reg-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g. Suresh Kumar"
                  required
                  className="h-10 text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reg-business" className="text-xs font-bold flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5 text-primary" /> Business / Shop Name
                </Label>
                <Input
                  id="reg-business"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="e.g. Sri Balaji Super Market"
                  required
                  className="h-10 text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reg-city" className="text-xs font-bold">
                  City / Market Hub
                </Label>
                <Input
                  id="reg-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Bengaluru"
                  className="h-10 text-sm"
                />
              </div>

              {/* Shop Detailed Address */}
              <div className="space-y-1.5">
                <Label htmlFor="reg-address" className="text-xs font-bold flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-primary" /> Shop Detailed Address / Landmark
                </Label>
                <Input
                  id="reg-address"
                  value={addressLine}
                  onChange={(e) => setAddressLine(e.target.value)}
                  placeholder="e.g. Shop #12, 1st Cross, Main Road, Bengaluru"
                  className="h-10 text-sm"
                />
              </div>

              {/* GPS Location Pin Capture */}
              <div className="rounded-control border border-border bg-muted/20 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold flex items-center gap-1.5 text-foreground">
                    <Navigation className="h-3.5 w-3.5 text-primary" /> Shop GPS Pin
                  </span>
                  {latitude !== null && longitude !== null ? (
                    <span className="text-2xs font-extrabold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-emerald-500" /> GPS Pinned
                    </span>
                  ) : (
                    <span className="text-2xs text-muted-foreground font-medium">Optional</span>
                  )}
                </div>
                {latitude !== null && longitude !== null ? (
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-muted-foreground font-mono text-2xs truncate">
                      {latitude.toFixed(5)}°, {longitude.toFixed(5)}°
                    </span>
                    <button
                      type="button"
                      onClick={handleCaptureLocation}
                      className="text-2xs font-bold text-cta hover:underline"
                    >
                      Update GPS
                    </button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCaptureLocation}
                    loading={locating}
                    className="w-full text-xs font-bold gap-1.5 h-9"
                  >
                    <Navigation className="h-3.5 w-3.5 text-primary" /> Capture Shop Location (GPS)
                  </Button>
                )}
              </div>

              {/* Shop Board / KYC Document Upload (Optional) */}
              <DocumentUpload
                label="Shop Front Photo / KYC Document"
                subtitle="Optional"
                value={photoUrl}
                onSuccess={(url) => setPhotoUrl(url)}
              />

              <div className="space-y-1.5">
                <Label htmlFor="reg-gstin" className="text-xs font-bold">
                  GSTIN Number (Optional)
                </Label>
                <Input
                  id="reg-gstin"
                  value={gstNumber}
                  onChange={(e) => setGstNumber(e.target.value)}
                  placeholder="29ABCDE1234F1Z5"
                  className="h-10 text-xs font-mono"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 font-black text-sm shadow-md bg-cta text-cta-foreground hover:brightness-105 active:scale-[0.99] rounded-control"
                loading={submitting}
              >
                Complete Registration & Enter App
                {!submitting && <ArrowRight className="h-4 w-4 ml-2" />}
              </Button>
            </form>
          )}

          {/* Official APMC Commodity Trend & Lowest Price Guarantee Instagram Card (Retailers Only) */}
          {isBuyerPortal && (

          <div className="p-3.5 rounded-control border border-pink-500/30 bg-gradient-to-r from-pink-500/10 via-amber-500/5 to-emerald-500/10 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-extrabold text-foreground">
                📸 Trusted Sourcing & QC-Tested Wholesale Rates
              </span>
              <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                Lowest Price Guaranteed
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Follow <strong>@mydailydirect.in</strong> on Instagram for daily wholesale rates, quality-tested staple updates & insights for Hotels, PGs, Bakeries, Retailers & Traders.
            </p>
            <a
              href="https://www.instagram.com/mydailydirect.in"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-gradient-to-r from-pink-600 to-amber-500 text-white text-xs font-extrabold shadow-2xs hover:opacity-95 transition-opacity"
            >
              <span>Follow @mydailydirect.in on Instagram</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
          )}

          {/* Security Badge */}
          <div className="pt-4 flex items-center justify-center gap-1.5 text-2xs text-muted-foreground border-t border-border">
            <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <span>Secured by Firebase Phone Auth & MyDailyDirect Encryption</span>
          </div>
        </div>
      </div>
    </div>
  );
}
